package com.milta.feature.importjobs.presentation

import com.milta.feature.importjobs.domain.entity.ImportJob
import com.milta.feature.importjobs.domain.entity.ImportJobErrorCodes
import com.milta.feature.importjobs.domain.entity.ImportJobStatus

/** Which error card a failed import shows — shared by the PDF, spreadsheet and photo OCR screens. */
enum class ImportJobFailureKind {
    /** The analysis service failed: same file can be relaunched server-side while an attempt remains. */
    SERVICE_UNAVAILABLE,
    /** The document itself is the problem: retrying the same file would give the same result. */
    UNREADABLE,
    TOO_LONG,
    TOO_LARGE,
    NO_DELIVERIES_FOUND,
    SOURCE_FILE_EXPIRED,
    /** Cancelled server-side: the route it was launched from was deleted. Only acknowledged, never retried. */
    ORIGIN_ROUTE_DELETED,
    /** MIL-98 — AI credit at 0 (job stopped at start, or 403 on upload / relaunch): "Réessayer" stays
     * disabled until the reset date (R6), a spreadsheet gets its dedicated message (R7). */
    AI_CREDIT_EXHAUSTED,
    ;

    val isDocumentProblem: Boolean get() = this != SERVICE_UNAVAILABLE
}

/** Cancelled by the server because its origin route was deleted: shown as a failure until acknowledged. */
val ImportJob.isOriginRouteDeleted: Boolean
    get() = status == ImportJobStatus.CANCELLED && errorCode == ImportJobErrorCodes.ORIGIN_ROUTE_DELETED

/** The import ended without a result and waits for the user: a failure, or the origin-route cancellation. */
val ImportJob.showsFailure: Boolean get() = status == ImportJobStatus.FAILED || isOriginRouteDeleted

/** Unknown codes fall back to [ImportJobFailureKind.SERVICE_UNAVAILABLE] (retry offered while an attempt remains). */
fun ImportJob.failureKind(): ImportJobFailureKind = when (errorCode) {
    ImportJobErrorCodes.ORIGIN_ROUTE_DELETED -> ImportJobFailureKind.ORIGIN_ROUTE_DELETED
    ImportJobErrorCodes.AI_CREDIT_EXHAUSTED -> ImportJobFailureKind.AI_CREDIT_EXHAUSTED
    "error.pdf_unreadable", "error.ocr_extraction_failed", "error.spreadsheet_extraction_failed" ->
        ImportJobFailureKind.UNREADABLE
    ImportJobErrorCodes.DOCUMENT_TOO_LONG -> ImportJobFailureKind.TOO_LONG
    ImportJobErrorCodes.DOCUMENT_TOO_LARGE -> ImportJobFailureKind.TOO_LARGE
    ImportJobErrorCodes.NO_DELIVERIES_FOUND -> ImportJobFailureKind.NO_DELIVERIES_FOUND
    ImportJobErrorCodes.SOURCE_FILE_EXPIRED -> ImportJobFailureKind.SOURCE_FILE_EXPIRED
    else -> ImportJobFailureKind.SERVICE_UNAVAILABLE
}

/** "Réessayer" (server-side relaunch, no upload) is offered only for a service failure with an attempt left. */
val ImportJob.offersServerRetry: Boolean get() = canRetry && !failureKind().isDocumentProblem
